/** Experimental Studio-observed own-user activity metadata, not transcript access. */
import { z } from "zod";
import type { AuthProvider } from "../auth/types.js";
import { RESOURCES } from "../auth/types.js";
import type { DataverseApi } from "./dataverse-api.js";
import { environmentIdToApiHost } from "./connections-api.js";
import { requestWithRetry } from "../utils/http.js";
import { ApiError } from "../utils/errors.js";

export const ACTIVITY_GUID_PATTERN =
  /^[0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}$(?![\s\S])/;
export const ACTIVITY_ENVIRONMENT_PATTERN =
  /^(?:[Dd][Ee][Ff][Aa][Uu][Ll][Tt]-)?[0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}$(?![\s\S])/;
const SCHEMA_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,199}$(?![\s\S])/;
const DEADLINE_MS = 10000;
const MAX_BYTES = 1024 * 1024;
const WIRE_PAGE_SIZE = 150;
const MAX_ROWS = 200;
export const listCopilotAgentActivityInputSchema = z
  .object({
    environment: z.string().regex(ACTIVITY_ENVIRONMENT_PATTERN),
    agentId: z.string().regex(ACTIVITY_GUID_PATTERN),
    limit: z.number().int().min(1).max(50).optional(),
  })
  .strict();
export type CopilotActivityInput = z.infer<typeof listCopilotAgentActivityInputSchema>;
type Reason =
  | "input"
  | "environment"
  | "authentication"
  | "identity"
  | "agent"
  | "http"
  | "response"
  | "deadline"
  | "unavailable";
const REASONS: Record<Reason, string> = {
  input: "invalid exact target or limit",
  environment: "explicit target must match the unchanged current Dataverse environment",
  authentication: "existing silent Power Platform authentication unavailable",
  identity: "selected account or returned activity ownership could not be verified",
  agent: "exact agent schema preflight unavailable or mismatched",
  http: "activity metadata request was refused",
  response: "activity metadata response is malformed or exceeds bounds",
  deadline: "activity metadata deadline exceeded",
  unavailable: "required API capability unavailable",
};
export class CopilotActivityError extends ApiError {
  constructor(reason: Reason, statusCode?: number) {
    const status =
      Number.isInteger(statusCode) && statusCode! >= 100 && statusCode! <= 599 ? statusCode! : 502;
    super(
      `Copilot activity metadata: ${REASONS[reason]}${reason === "http" ? ` (HTTP ${status})` : ""}. No activity rows returned; no retry, fallback, sign-in or tenant change was attempted.`,
      status,
    );
    this.name = "CopilotActivityError";
  }
}
function object(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
function canonicalEnvironment(value: string) {
  return /^default-/i.test(value) ? `Default-${value.slice(8).toLowerCase()}` : value.toLowerCase();
}
function timestamp(value: unknown): string | null {
  if (typeof value !== "string" || value.length > 28) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,7})?Z$(?![\s\S])/.exec(
    value,
  );
  if (!match) return null;
  const [year, month, day, hour, minute, second] = match.slice(1).map(Number) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return year > 0 &&
    month >= 1 &&
    month <= 12 &&
    day > 0 &&
    day <= days[month - 1]! &&
    hour < 24 &&
    minute < 60 &&
    second < 60 &&
    Number.isFinite(Date.parse(value))
    ? value
    : null;
}
function count(value: unknown, max: number): number | null {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 && value <= max
    ? value
    : null;
}
function fieldState(value: unknown, valid: unknown) {
  return valid !== null
    ? "available"
    : value === undefined
      ? "absent"
      : value === null
        ? "null"
        : "unsupported";
}

export class CopilotActivityApi {
  constructor(
    private readonly authProvider: AuthProvider,
    private readonly dataverseApi: Pick<DataverseApi, "getCopilotAgentIdentitySilent">,
    private readonly currentEnvironmentId: () => string,
  ) {}

  async listActivity(input: CopilotActivityInput) {
    const validated = listCopilotAgentActivityInputSchema.safeParse(input);
    if (!validated.success) throw new CopilotActivityError("input");
    const environment = canonicalEnvironment(validated.data.environment);
    const agentId = validated.data.agentId.toLowerCase();
    const limit = validated.data.limit ?? 20;
    const controller = new AbortController();
    let body: Awaited<ReturnType<typeof requestWithRetry>>["body"] | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const destroy = () => {
      try {
        body?.destroy();
      } catch {
        /* Safe cleanup only. */
      }
    };
    const active = () => {
      if (controller.signal.aborted) {
        destroy();
        throw new CopilotActivityError("deadline");
      }
    };
    const sameEnvironment = () => {
      const current = this.currentEnvironmentId();
      if (
        typeof current !== "string" ||
        !ACTIVITY_ENVIRONMENT_PATTERN.test(current) ||
        canonicalEnvironment(current) !== environment
      )
        throw new CopilotActivityError("environment");
    };
    const work = async () => {
      sameEnvironment();
      if (
        !this.authProvider.tryGetTokenForResourceSilent ||
        !this.dataverseApi.getCopilotAgentIdentitySilent
      )
        throw new CopilotActivityError("unavailable");
      const before = this.authProvider.getCurrentAccount();
      if (
        !before ||
        typeof before.username !== "string" ||
        !before.username.trim() ||
        typeof before.tenantId !== "string" ||
        !ACTIVITY_GUID_PATTERN.test(before.tenantId) ||
        typeof before.localAccountId !== "string" ||
        !ACTIVITY_GUID_PATTERN.test(before.localAccountId)
      )
        throw new CopilotActivityError("identity");
      const selected = {
        username: before.username,
        tenantId: before.tenantId,
        objectId: before.localAccountId,
      };
      const sameAccount = () => {
        const now = this.authProvider.getCurrentAccount();
        if (
          !now ||
          now.username !== selected.username ||
          now.tenantId !== selected.tenantId ||
          now.localAccountId !== selected.objectId
        )
          throw new CopilotActivityError("identity");
      };
      let bot: unknown;
      try {
        bot = await this.dataverseApi.getCopilotAgentIdentitySilent(agentId, {
          signal: controller.signal,
        });
      } catch {
        throw new CopilotActivityError("agent");
      }
      active();
      sameEnvironment();
      sameAccount();
      if (
        !object(bot) ||
        typeof bot.botid !== "string" ||
        !ACTIVITY_GUID_PATTERN.test(bot.botid) ||
        bot.botid.toLowerCase() !== agentId ||
        typeof bot.schemaname !== "string" ||
        !SCHEMA_PATTERN.test(bot.schemaname)
      )
        throw new CopilotActivityError("agent");
      const schema = bot.schemaname;
      let token: unknown;
      try {
        token = await this.authProvider.tryGetTokenForResourceSilent(RESOURCES.POWER_PLATFORM_API);
      } catch {
        throw new CopilotActivityError("authentication");
      }
      active();
      sameEnvironment();
      sameAccount();
      if (typeof token !== "string" || !token.trim() || /\s/.test(token))
        throw new CopilotActivityError("authentication");
      const query = new URLSearchParams({
        "api-version": "1",
        botSchemaName: schema,
        pageSize: String(WIRE_PAGE_SIZE),
      });
      const response = await requestWithRetry(
        `https://${environmentIdToApiHost(environment)}/powervirtualagents/conversations/metadata?${query.toString()}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
          signal: controller.signal,
          headersTimeout: DEADLINE_MS,
          bodyTimeout: DEADLINE_MS,
        },
        { maxAttempts: 1 },
      );
      body = response.body;
      active();
      sameEnvironment();
      sameAccount();
      if (response.statusCode !== 200) throw new CopilotActivityError("http", response.statusCode);
      const contentType = response.headers["content-type"];
      if (
        typeof contentType !== "string" ||
        !/^application\/(?:json|[a-z0-9.+-]+\+json)(?:\s*;|$)/i.test(contentType)
      )
        throw new CopilotActivityError("response");
      const chunks: Buffer[] = [];
      let size = 0;
      for await (const chunk of body as AsyncIterable<Uint8Array>) {
        active();
        const bytes = Buffer.from(chunk);
        size += bytes.length;
        if (size > MAX_BYTES) throw new CopilotActivityError("response");
        chunks.push(bytes);
      }
      active();
      sameEnvironment();
      sameAccount();
      const data: unknown = JSON.parse(Buffer.concat(chunks).toString("utf8"));
      if (!object(data) || !Array.isArray(data.entities) || data.entities.length > MAX_ROWS)
        throw new CopilotActivityError("response");
      const expectedAgent = `${environment}-${schema}`;
      const expectedUser = `8:orgid:${selected.objectId.toLowerCase()}`;
      // Verify every returned row, even beyond the display limit, before exposing any metadata.
      for (const row of data.entities) {
        if (
          !object(row) ||
          row.agentId !== expectedAgent ||
          typeof row.userId !== "string" ||
          row.userId.toLowerCase() !== expectedUser ||
          typeof row.conversationId !== "string" ||
          !ACTIVITY_GUID_PATTERN.test(row.conversationId)
        )
          throw new CopilotActivityError("identity");
      }
      const activities = (data.entities as Record<string, unknown>[]).slice(0, limit).map((row) => {
        const statusCode = count(row.status, 255);
        const channelId =
          row.channelId === "pva-studio" || row.channelId === "pva-maker-evaluation"
            ? row.channelId
            : null;
        const startDate = timestamp(row.startDate),
          modifiedDate = timestamp(row.modifiedDate);
        const stepCount = count(row.stepCount, 1000000),
          blockedStepCount = count(row.stepBlockedCount, 1000000);
        return {
          conversationId: (row.conversationId as string).toLowerCase(),
          startDate,
          startDateState: fieldState(row.startDate, startDate),
          modifiedDate,
          modifiedDateState: fieldState(row.modifiedDate, modifiedDate),
          statusCode,
          statusState: fieldState(row.status, statusCode),
          channelId,
          channelState: fieldState(row.channelId, channelId),
          stepCount,
          stepCountState: fieldState(row.stepCount, stepCount),
          blockedStepCount,
          blockedStepCountState: fieldState(row.stepBlockedCount, blockedStepCount),
        };
      });
      return {
        experimental: true,
        source: "studio-own-user-activity-metadata",
        environment,
        agentId,
        ownerMatchedCurrentUser: true,
        allReturnedRowsIdentityMatched: true,
        activities,
        returnedCount: activities.length,
        sourceRowCount: data.entities.length,
        sourcePageSize: count(data.pageSize, MAX_ROWS),
        requestedWirePageSize: WIRE_PAGE_SIZE,
        localLimit: limit,
        locallyTruncated: data.entities.length > limit,
        morePagesReported: typeof data.hasMore === "boolean" ? data.hasMore : null,
        continuation: "not-returned-or-followed",
        totalCount: null,
        notice:
          "One first page only, not an exhaustive activity inventory. Wire pageSize is 150; local output is limited separately. Numeric statusCode values are unmapped Studio codes, not success/failure or runtime verdicts. All returned rows matched the selected user and exact agent. No summaries, user IDs, nested steps, transcripts, continuation tokens or private URLs are returned. Metadata does not prove a ticket action succeeded.",
      };
    };
    try {
      return await Promise.race([
        work(),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => {
            controller.abort();
            destroy();
            reject(new CopilotActivityError("deadline"));
          }, DEADLINE_MS);
        }),
      ]);
    } catch (error) {
      if (error instanceof CopilotActivityError) throw error;
      throw new CopilotActivityError("response");
    } finally {
      clearTimeout(timer);
      controller.abort();
      destroy();
    }
  }
}
